import React, { useEffect, useState } from "react";
import { History, ShieldOff, Mail, AlertTriangle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import JeaCard from "@/components/jea/JeaCard";
import { adminRecoveryApi, formatApiError } from "@/lib/apiClient";

const EVENT_META = {
  "admin.reset_2fa":            { icon: ShieldOff,     label: "Reset 2FA",            tone: "bg-warning/15 text-warning border-warning/40" },
  "admin.send_password_reset":  { icon: Mail,          label: "Password Reset",       tone: "bg-success/15 text-success border-success/40" },
  "admin.full_recovery":        { icon: AlertTriangle, label: "Full Recovery",        tone: "bg-danger/15 text-danger border-danger/40" },
};

const RecoveryAuditLogPanel = ({ refreshKey }) => {
  const [events, setEvents] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const res = await adminRecoveryApi.auditLog(50, 0);
        if (cancelled) return;
        setEvents(res.events || []);
        setTotal(res.total || 0);
      } catch (err) {
        if (!cancelled) setError(formatApiError(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [refreshKey]);

  if (loading) {
    return <div className="p-6 text-text-secondary text-[14px]" data-testid="recovery-audit-loading">Loading audit log…</div>;
  }
  if (error) {
    return <div className="p-6 text-danger text-[14px]" data-testid="recovery-audit-error">{error}</div>;
  }
  if (!events.length) {
    return (
      <JeaCard className="p-10 text-center" data-testid="recovery-audit-empty">
        <History className="w-10 h-10 text-text-muted mx-auto mb-3" />
        <h3 className="text-[16px] font-semibold text-white">No recovery actions yet</h3>
        <p className="text-[13px] text-text-secondary mt-1">
          When administrators reset 2FA or send password reset links, they'll appear here.
        </p>
      </JeaCard>
    );
  }

  return (
    <JeaCard className="p-0 overflow-hidden" data-testid="recovery-audit-table">
      <div className="px-5 py-3 border-b border-border-default flex items-center justify-between">
        <div className="flex items-center gap-2">
          <History className="w-4 h-4 text-text-secondary" />
          <h3 className="text-[14px] font-semibold text-white">Account Recovery Audit Log</h3>
        </div>
        <div className="text-[11px] font-mono text-text-muted">{total} total · showing {events.length}</div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[13px]">
          <thead>
            <tr className="border-b border-border-default bg-bg-primary/40">
              <th className="text-left px-4 py-2 font-semibold text-text-muted text-[11px] uppercase tracking-wider">Timestamp</th>
              <th className="text-left px-4 py-2 font-semibold text-text-muted text-[11px] uppercase tracking-wider">Admin</th>
              <th className="text-left px-4 py-2 font-semibold text-text-muted text-[11px] uppercase tracking-wider">Target</th>
              <th className="text-left px-4 py-2 font-semibold text-text-muted text-[11px] uppercase tracking-wider">Action</th>
              <th className="text-left px-4 py-2 font-semibold text-text-muted text-[11px] uppercase tracking-wider">Reason</th>
              <th className="text-left px-4 py-2 font-semibold text-text-muted text-[11px] uppercase tracking-wider">IP</th>
            </tr>
          </thead>
          <tbody>
            {events.map((e) => {
              const meta = EVENT_META[e.event_type] || { icon: History, label: e.event_type, tone: "bg-bg-tertiary text-text-secondary" };
              const Icon = meta.icon;
              const reason = (e.metadata?.reason || "").trim();
              const reasonShort = reason.length > 60 ? reason.slice(0, 60) + "…" : reason;
              const ip = e.metadata?.ip || "—";
              return (
                <tr key={e.id} className="border-b border-border-default/60 hover:bg-bg-tertiary/40 transition-colors" data-testid={`recovery-audit-row-${e.id}`}>
                  <td className="px-4 py-3 font-mono text-text-secondary text-[12px] whitespace-nowrap">{new Date(e.created_at).toLocaleString()}</td>
                  <td className="px-4 py-3 text-white whitespace-nowrap">{e.admin_name || e.admin_id}</td>
                  <td className="px-4 py-3 text-text-secondary whitespace-nowrap">{e.target_label || e.target_id}</td>
                  <td className="px-4 py-3">
                    <Badge className={`${meta.tone} h-5 text-[10px] font-semibold tracking-wider uppercase whitespace-nowrap`}>
                      <Icon className="w-2.5 h-2.5 mr-1" /> {meta.label}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-text-secondary" title={reason}>{reasonShort}</td>
                  <td className="px-4 py-3 font-mono text-[11px] text-text-muted">{ip}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </JeaCard>
  );
};

export default RecoveryAuditLogPanel;
